import { makeStyles } from '@material-ui/core';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import filesAPI from '../../api/files';
import { Mentor } from '../../api/mentors';
import { PageLoader } from '../../components/common';
import MentorCardProfile from '../../components/mentor-card/mentor-card-profile';
import { MentorContext, MentorProvider } from '../../contexts/mentor-context';
import { SpecializationContext } from '../../contexts/specialization-context';
import { COLORS, INTER_FONT_FAMILY } from '../../theme/variables';

const useStyles = makeStyles({
  centeredPage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  notFound: {
    fontFamily: `${INTER_FONT_FAMILY}, sans-serif`,
    color: COLORS.COLOR_TEXT_BASE,
  },
});

function MentorCardPage() {
  const classes = useStyles();
  const { mentorId } = useParams<{ mentorId: Mentor['id'] }>();
  const {
    mentor,
    isMentorLoading,
    mentorSpecializations,
    loadMentor,
    loadMentorSpecializations,
  } = useContext(MentorContext);
  const { specializations, loadSpecializations } =
    useContext(SpecializationContext);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!mentor) {
      loadMentor(mentorId);
    }
  }, [mentorId, mentor, loadMentor]);

  useEffect(() => {
    if (specializations.length === 0) {
      loadSpecializations();
    }
  }, [specializations, loadSpecializations]);

  useEffect(() => {
    if (!mentorSpecializations) {
      loadMentorSpecializations(mentorId);
    }
  }, [mentorSpecializations, mentorId, loadMentorSpecializations]);

  useEffect(() => {
    if (!mentor?.logo) {
      setLogoUrl(null);
      return;
    }

    let isActive = true;
    setLogoUrl(null);
    filesAPI
      .getFileLogo(mentor.logo)
      .then((url) => {
        if (isActive) {
          setLogoUrl(url);
        }
      })
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, [mentor?.logo]);

  const isLoading = useMemo(
    () =>
      isMentorLoading ||
      mentorSpecializations === null ||
      specializations.length === 0,
    [isMentorLoading, mentorSpecializations, specializations.length],
  );

  const mentorSpecNames = useMemo(
    () =>
      (mentorSpecializations || [])
        .map((specId) => specializations.find((s) => s.id === specId))
        .filter((s): s is NonNullable<typeof s> => s != null)
        .map((s) => s.name),
    [mentorSpecializations, specializations],
  );

  if (isLoading) {
    return (
      <div className={classes.centeredPage}>
        <PageLoader />
      </div>
    );
  }

  if (!mentor) {
    return (
      <div className={`${classes.centeredPage} ${classes.notFound}`}>
        Mentor not found
      </div>
    );
  }


  return (
    <MentorCardProfile
      mentor={mentor}
      logoUrl={logoUrl}
      specializations={mentorSpecNames}
    />
  );
}

function withMentorProvider(Component: React.FunctionComponent) {
  return () => (
    <MentorProvider>
      <Component />
    </MentorProvider>
  );
}

export default withMentorProvider(MentorCardPage);
